
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { Card } from "react-bootstrap";

import { useAuth } from "@/Hooks/UseAuth";
import useTranslation from "@/Hooks/UseTranslation";
import { useProject } from "@/Hooks/UseProject";
import { useProgrammation } from "@/Hooks/UseProgrammation";
import { useProjection } from "@/Hooks/ProjectionProvider";
import { useTeams } from "@/Hooks/UseTeam";

import { getWeekNavigator } from "@/Helpers/LookaheadHelper";
import TeamsHelper from "@/Helpers/TeamsHelper";
import { maxDate } from "@/Helpers/DateFunctions";
import {
  IGetItemsListOptions,
  filterByColorCodes,
  filterByGroups,
  filterByStatus,
  filterByTypes,
  filterByUsers,
  getCollapsedGroups,
  getCollapsedTasks,
  getGanttItems,
  getLookaheadResult,
  getNonWorkingDays,
  saveCollapsedGroups,
  saveCollapsedTasks
} from "@/Helpers/ProgrammationHelper";

import ProjectsActions from "@/Redux/Actions/ProjectsActions";
import {
  regenerateLookahead,
  setLookaheadAction,
  setLookaheadCollapsedGroupsAction,
  setLookaheadCollapsedTasksAction,
  setLookeaheadGanttItemsAction
} from "@/Redux/Actions/LookaheadActions";
import InterfaceActions from "@/Redux/Actions/InterfaceActions";

import ProjectLayout from "@/Layouts/MainLayout/ProjectLayout";
import ContentBody from "@/Layouts/MainLayout/components/ContentBody";
import ProgrammationHeader from "../ProgrammationHeader";
import ProgrammationCreateMenu from "../ProgrammationCreateMenu";
import WeekNavigator from "@/Components/WeekNavigator/WeekNavigator";
// import TasksImporterUnified from "@/Components/TasksImporterUnified/TasksImporterUnified";

import Gantt from "@/Components/GantV2/Gantt";
import { GanttColumn } from "@/Components/GantV2/types";
import { getStatusName } from "@/Components/TaskManager/TaskEditorHelper";
import UserAvatar from "@/Components/UserAvatar/UserAvatar";
import TaskFilters from "@/Components/TaskFilters/TaskFilters";
import PdfExportModal from "@/Components/PdfExportModal/PdfExportModal";
import ProgrammationManager from "@/Components/ProgrammationManager/ProgrammationManager";
import ResponsivePageToolbar from "@/Layouts/MainLayout/components/ResponsivePageToolbar";

const LookaheadPage = (props: any) => {

  const { t } = useTranslation()

  const sidebarRefBtn = useRef<HTMLButtonElement>(null)

  const week = props.match.params.week

  const { userData, ability } = useAuth()

  const dispatch = useDispatch()

  const { project_id, project, projectLoaded, loadProject } = useProject()
  const { programmationLoaded, loadProgrammation, openItem, createNewTask, createNewGroup } = useProgrammation()
  const { projectedEndDate, extendedNonWorkingDays } = useProjection()

  const { teams, allInternalProfiles } = useTeams()

  const programmation = useSelector((state: any) => state.ProgrammationReducer)
  const lookahead = useSelector((state: any) => state.LookaheadReducer)

  const teamsMembersIds = useMemo(() => TeamsHelper.getAllMembersFromProject(teams).map((user: any) => user._id), [ teams ])

  const [ showTaskFilters, setShowTaskFilters ] = useState(false)
  const [ showTasksImporter, setShowTasksImporter ] = useState(false)

  const savedFilters = localStorage.getItem(`${project_id}-lookahead-filters`)

  const [ filters, setFilters ] = useState(savedFilters ? JSON.parse(savedFilters) : {
    user: '',
    state: '',
    team: '',
    group: '',
    code: '',
    type: '',
    start: '',
    end: '',
  })

  const [ mySettings, setMySettings ] = useState(project?.mySettings)
  const [ showSidebar, setShowSidebar ] = useState(false)


  const navigator = useMemo(() => {

    if (!project._id) return {
      prev: '',
      next: '',
      startDay: '',
      endDay: '',
      week: 0,
      range: []
    }

    return getWeekNavigator({
      projectStart: project.startsAt,
      projectEnd: maxDate(project.realEnd, projectedEndDate) || project.realEnd,
      weeksSize: project.settings.lookahead,
      week
    })
  }, [ project, week, projectedEndDate ])

  const navigateToWeek = (week?: any) => {

    dispatch(regenerateLookahead())

    if (week) {

      props.history.push(`/project/${project_id}/programmation-lookahead/${week}`)
    } else {

      props.history.push(`/project/${project_id}/programmation-lookahead`)
    }
  }

  const nonWorkingDays = useMemo(() => {

    if (!project._id) return []

    return getNonWorkingDays(lookahead.start, lookahead.end, project)
  }, [ project._id, lookahead.start, lookahead.end ])

  const [ showPdfExportModal, setShowPdfExportModal ] = useState(false)

  const handleTaskListImported = () => {
    loadProject()
    loadProgrammation()
    setShowTasksImporter(false)
  }

  const columns: GanttColumn[] = [
    {
      label: t('LOOKAHEAD:TypeColumn'),
      field: 'type',
      width: 50,
      render: (field: any) => {

        if (!field) return null

        if (field.toLowerCase() === 'trabajo') return 'W'

        return field.substring(0, 1).toUpperCase()
      }
  },
    {
      label: t('LOOKAHEAD:ColorCodeColumn'),
      field: 'colorCodeName',
      width: 60,
    },
    {
      label: t('LOOKAHEAD:PositionColumn'),
      field: 'position',
      width: 100,
    },
    {
      label: t('LOOKAHEAD:TitleColumn'),
      field: 'name',
      width: 300,
    },
    {
      label: t('LOOKAHEAD:StatusColumn'),
      field: 'status',
      width: 100,
      render: (field: any) => getStatusName(field)
    },
    {
      label: t('LOOKAHEAD:StartsAtColumn'),
      field: 'startsAt',
      width: 80,
      render: (field: any) => field ? moment(field).format('DD/MM/YYYY') : null
    },
    {
      label: t('LOOKAHEAD:EndsAtColumn'),
      field: 'endsAt',
      width: 80,
      render: (field: any) => field ? moment(field).format('DD/MM/YYYY') : null
    },
    {
      label: t('LOOKAHEAD:ResponsiblesColumn'),
      field: 'responsibles',
      width: 80,
      render: (field: any, item: any) => {

        const isField = !!field
        const isFieldArray = isField && Array.isArray(field)

        if (isField && isFieldArray) {

          const responsibles = field.filter((user: any) => teamsMembersIds.includes(user._id))

          return responsibles.map((user: any) => <UserAvatar key={user._id} user={user} size="xs" />)
        }

        return null
      }
    },
    {
      label: t('LOOKAHEAD:ReleasesAtColumn'),
      field: 'releasesAt',
      width: 80,
      render: (field: any) => field ? moment.utc(field).format('DD/MM/YYYY') : null
    },
    {
      label: t('LOOKAHEAD:ExecuteDaysColumn'),
      field: 'executeDays',
      width: 60,
    },
    {
      label: t('LOOKAHEAD:PeriodDaysColumn'),
      field: 'periodDays',
      width: 100,
    },
    {
      label: t('LOOKAHEAD:ExecutePercentColumn'),
      field: 'executePercent',
      width: 60,
    },
    {
      label: t('LOOKAHEAD:UnitColumn'),
      field: 'unit',
      width: 60,
    },
    {
      label: t('LOOKAHEAD:MeteredColumn'),
      field: 'metered',
      width: 60,
      render: (field: any) => field || ''
    },
    {
      label: t('LOOKAHEAD:PerformanceColumn'),
      field: 'performance',
      width: 60,
      render: (field: any) => field || ''
    },
    {
      label: t('LOOKAHEAD:CrewNumberColumn'),
      field: 'crewNumber',
      width: 60,
      render: (field: any) => field || ''
    },
    {
      label: t('LOOKAHEAD:EstimatedDaysColumn'),
      field: 'estimatedDays',
      width: 60,
      render: (field: any) => field || ''
    },
    {
      label: t('LOOKAHEAD:ActualStartColumn'),
      field: 'startedAt',
      width: 90,
      render: (field: any) => field ? moment(field).format('DD/MM/YYYY') : null
    },
    {
      label: t('LOOKAHEAD:ActualEndColumn'),
      field: 'endedAt',
      width: 90,
      render: (field: any) => field ? moment(field).format('DD/MM/YYYY') : null
    },
    {
      label: t('LOOKAHEAD:RestrictionsColumn'),
      field: 'restrictions',
      width: 90,
      render: (field: any) => !field ? null : `${field.done}/${field.toDo}`
    },
    {
      label: t('LOOKAHEAD:CommentsColumn'),
      field: 'comments',
      width: 150,
      render: (field: any) => {

        if (!field) return null

        return `${field.comment}...`
      }
    }
  ]

  const loadLookahead = (withFilters?: any) => {

    if (programmation.loadStatus !== 'loaded') return

    const tasks = programmation.tasks.map((task: any) => {

      const responsibles = task.responsibles.map((resp: any) => programmation.users.find((user: any) => user._id === resp))

      return {
        ...task,
        responsibles,
        internalProfiles: task.internalProfiles.map((resp: any) => allInternalProfiles.find((profile: any) => profile._id === resp)),
      }
    })

    const options = {
      ...programmation,
      list: JSON.parse(JSON.stringify(programmation.list)),
      tasks: JSON.parse(JSON.stringify(tasks)),
      navigator,
      nonWorkingDays: project.nonWorkingDays,
    }

    let lookaheadResult = getLookaheadResult(options)

    const newFilters = withFilters || filters

    if (newFilters.team) {

      const teams_ids = newFilters.team.split(',')

      const teamsFound = teams.filter((team: any) => teams_ids.includes(team._id))

      const users_ids: string[] = []

      teamsFound.forEach((team: any) => {

        team.users.forEach((user: any) => {

          if (!users_ids.includes(user._id))
            users_ids.push(user._id)
        })
      })

      lookaheadResult = filterByUsers(lookaheadResult, users_ids)
    } else if (newFilters.user)
      lookaheadResult = filterByUsers(lookaheadResult, newFilters.user.split(','))

    if (newFilters.group) {

      lookaheadResult = filterByGroups(lookaheadResult, newFilters.group.split(','))
    } else if (newFilters.code)
      lookaheadResult = filterByColorCodes(lookaheadResult, newFilters.code.split(','))

    if (newFilters.type)
      lookaheadResult = filterByTypes(lookaheadResult, newFilters.type.split(','))

    if (newFilters.state)
      lookaheadResult = filterByStatus(lookaheadResult, newFilters.state.split(','))

    const collapsedGroups = getCollapsedGroups(project_id, 'lookahead')
    const collapsedTasks = getCollapsedTasks(project_id, 'lookahead')

    dispatch(setLookaheadAction(lookaheadResult, navigator.startDay, navigator.endDay, navigator.week, collapsedGroups, collapsedTasks))
  }

  const hasActiveFilters = () => {

    const values = Object.values(filters)
    const found = values.findIndex(f => f !== '')

    return !!(found >= 0)
  }

  const handleChangeFilters = (filters: any) => {

    localStorage.setItem(`${project_id}-lookahead-filters`, JSON.stringify(filters))

    loadLookahead(filters)
    setFilters(filters)
  }

  const handleEditItem = async (item_id: string) => {

    openItem({ item_id, schema: 'lookahead' })
  }

  const handleToggleCollapse = async (group_id: string) => {

    const collapsedGroups = getCollapsedGroups(project_id, 'lookahead')

    if (collapsedGroups.includes(group_id)) {

      collapsedGroups.splice(collapsedGroups.indexOf(group_id), 1)
    } else {

      collapsedGroups.push(group_id)
    }

    saveCollapsedGroups(project_id, 'lookahead', collapsedGroups)

    dispatch(setLookaheadCollapsedGroupsAction(collapsedGroups))
  }

  const handleToggleCollapseTask = async (task_id: string) => {

    const collapsedTasks = getCollapsedTasks(project_id, 'lookahead')

    if (collapsedTasks.includes(task_id)) {

      collapsedTasks.splice(collapsedTasks.indexOf(task_id), 1)
    } else {

      collapsedTasks.push(task_id)
    }

    saveCollapsedTasks(project_id, 'lookahead', collapsedTasks)

    dispatch(setLookaheadCollapsedTasksAction(collapsedTasks))
  }

  const handleShowWorkflows = (groups: string[], tasks: any[]) => {

    saveCollapsedGroups(project_id, 'lookahead', groups)
    saveCollapsedTasks(project_id, 'lookahead', tasks)

    dispatch(setLookaheadCollapsedGroupsAction(groups))
    dispatch(setLookaheadCollapsedTasksAction(tasks))
  }

  const updateSettings = async (settings = mySettings) => {

    let areDifferent = false

    for (const key in settings) {

      if (key !== 'setted' && settings[ key ] !== project.mySettings[ key ]) {

        areDifferent = true
      }
    }

    if (areDifferent) {

      await dispatch(ProjectsActions.updateSettings(project.mySettings._id, settings))
    }

    setMySettings(settings)
  }

  const toggleSidebar = (target?: any) => {

    if (!target) {

      if (showSidebar) updateSettings()

      setShowSidebar(!showSidebar)
    } else {

      if (sidebarRefBtn.current) {

        const isButtonClicked = sidebarRefBtn.current.contains(target) || target.closest?.('.pl4-settings-trigger')

        if (!isButtonClicked) {

          // trigger click because if not mySettings is not updated
          sidebarRefBtn.current.click()
        }
      }
    }
  }

  const handleUpdateSetting = (key: string, value: any) => {

    let field = key

    if (field === 'dayWidth') {

      field = 'ganttLookaheadColumnSize'
    } else if (field === 'hideNonWorkingDays') {

      field = 'ganttLookaheadHideNonWorkingDays'
    } else if (field === 'showVerticalBorders') {

      field = 'ganttLookaheadShowVerticalBorders'
    } else if (field === 'columnsHidden') {

      field = 'ganttHideLookaheadFields'
    } else if (field === 'divisorPosition') {

      field = 'ganttLookaheadDivisorPosition'
    }

    updateSettings({
      ...mySettings,
      [ field ]: value
    })
  }

  useEffect(() => {

    if (projectLoaded) {

      setMySettings(project.mySettings)
    }
  }, [ projectLoaded ])

  useEffect(() => {

    if (!programmationLoaded) return

    if (lookahead.loadStatus === 'none') {

      loadLookahead()
    }
  }, [ programmationLoaded, lookahead.loadStatus ])

  // ganttitems
  useEffect(() => {

    if (lookahead.loadStatus !== 'loaded') return
    if (lookahead.loadGanttItemsStatus !== 'none') return

    const options: IGetItemsListOptions = {
      itemsList: lookahead.result,
      collapsedGroups: lookahead.collapsedGroups,
      collapsedTasks: lookahead.collapsedTasks,
      schema: 'lookahead',
      nonWorkingDays: extendedNonWorkingDays,
      stretchUpToday: mySettings?.stretchUpToday,
      moveToScheduledDays: mySettings?.moveToScheduledDays,
      showOnlyThisUserTasks: project.mySettings.showOnlyMyTasks ? userData?.id : undefined,
      week: navigator.week,
      ganttStart: lookahead.start,
      ganttEnd: lookahead.end,
    }

    const ganttItems = getGanttItems(options);

    // cortamos las barras de los items que se salen del rango de fechas
    ganttItems.forEach((item: any) => {

      item.barStart = moment(item.barStart).isBefore(moment(lookahead.start)) ? lookahead.start : item.barStart;
      item.barEnd = moment(item.barEnd).isAfter(moment(lookahead.end)) ? lookahead.end : item.barEnd;

      item.bars?.forEach((bar: any) => {

        bar.barStart = moment(bar.barStart).isBefore(moment(lookahead.start)) ? lookahead.start : bar.barStart;
        bar.barEnd = moment(bar.barEnd).isAfter(moment(lookahead.end)) ? lookahead.end : bar.barEnd;
      })
    })

    dispatch(setLookeaheadGanttItemsAction(ganttItems))
  }, [ lookahead.loadGanttItemsStatus, lookahead.result, mySettings ]);

  useEffect(() => {

    const taskListLoaded = lookahead.loadStatus === 'loaded'

    if (!projectLoaded || !taskListLoaded) {

      dispatch(InterfaceActions.showLoader())
    } else {

      dispatch(InterfaceActions.hideLoader())
    }
  }, [ projectLoaded, lookahead.loadStatus ])

  return (
    <ProjectLayout menuName="programmation" subMenuName="lookahead" hideSubMenu>
      <ProgrammationHeader
        activeTab="lookahead"
        projectId={project_id}
        subtitle={`${t('LOOKAHEAD:Title')} · ${project.name || ''}`}
      >
        {/* <div className='ul-widget__head-label flex-grow-1'>
          <h3 className='ul-widget__head-title'>
            <span>
              {t('LOOKAHEAD:Title')}
            </span>
            {(lookahead.start !== '' && lookahead.end !== '') && (
              <small className="d-block d-sm-inline">
                ({t('LOOKAHEAD:DateFrom')} {' '}
                {moment(lookahead.start).format('DD/MM/YYYY')} {' '}
                {t('LOOKAHEAD:DateTo')} {' '}
                {moment(lookahead.end).format('DD/MM/YYYY')})
              </small>
            )}
          </h3>
        </div> */}
        <ResponsivePageToolbar
          className="pl4-programmation-toolbar"
          actionsClassName="pl4-programmation-toolbar__actions"
          primaryAction={ability.can('create', 'task') ? (
              <ProgrammationCreateMenu
                disabled={!projectLoaded || !project._id || !programmationLoaded}
                importDisabled={programmation.list.length > 0 || hasActiveFilters()}
                onCreateTask={() => createNewTask()}
                onCreateGroup={() => createNewGroup()}
                onImport={() => setShowTasksImporter(true)}
              />
          ) : undefined}
          temporalControl={(
            <WeekNavigator
              prev={navigator.prev}
              next={navigator.next}
              current={navigator.week}
              label={`${t('RESOURCES_CNC:TitleWeek')} ${navigator.week ? Number(String(navigator.week).slice(4)) : ''}`}
              range={navigator.startDay && navigator.endDay ? `${moment(navigator.startDay).format('DD/MM')} - ${moment(navigator.endDay).format('DD/MM/YYYY')}` : ''}
              startDate={navigator.startDay}
              endDate={navigator.endDay}
              onChangeWeek={navigateToWeek}
            />
          )}
          actions={[
            {
              id: 'filter',
              label: t('LOOKAHEAD:FilterButton'),
              icon: <span className="icon-filtro" aria-hidden="true" />,
              onClick: () => setShowTaskFilters(true),
              active: hasActiveFilters(),
              className: 'pl4-programmation-action',
            },
            {
              id: 'export',
              label: t('WEEKLY:ExportButton'),
              icon: <i className="fas fa-download" aria-hidden="true" />,
              onClick: () => setShowPdfExportModal(true),
              className: 'pl4-programmation-action',
            },
            {
              id: 'settings',
              label: t('LOOKAHEAD:SettingsButton'),
              icon: <span className="icon-configuracion" aria-hidden="true" />,
              onClick: () => toggleSidebar(),
              active: showSidebar,
              buttonRef: sidebarRefBtn,
              className: 'pl4-programmation-action pl4-settings-trigger',
              mobileClassName: 'pl4-settings-trigger',
            },
          ]}
        />
      </ProgrammationHeader>
      <ContentBody noScroll>
        <Card className="table-card" style={{ height: '100%' }}>
          {project._id && (
            <Gantt
              schema="lookahead"
              start={lookahead.start}
              end={lookahead.end}
              items={lookahead.ganttItems}
              columns={columns}
nonWorkingDays={extendedNonWorkingDays}
              dayWidth={mySettings?.ganttLookaheadColumnSize || 40}
              rowHeight={32}
              maxRowHeight={32}
              hideNonWorkingDays={mySettings?.ganttLookaheadHideNonWorkingDays || false}
              showVerticalBorders={mySettings?.ganttLookaheadShowVerticalBorders || false}
              divisorPosition={mySettings?.ganttLookaheadDivisorPosition}
              useCodeColors={mySettings?.useCodeColors || false}
              stretchUpToday={mySettings?.stretchUpToday || false}
              moveToScheduledDays={mySettings?.moveToScheduledDays || false}
              columnsHidden={mySettings?.ganttHideLookaheadFields || []}
              showSidebar={showSidebar}
              onSelectItem={handleEditItem}
              onToggleCollapse={handleToggleCollapse}
              onToggleCollapseTask={handleToggleCollapseTask}
              onShowWorkflows={handleShowWorkflows}
              onClickOutside={toggleSidebar}
              onUpdateSetting={handleUpdateSetting}
              sortEnabled={false}
              lineBaseTasks={false}
            />
          )}
        </Card>
      </ContentBody>

      <TaskFilters
        show={showTaskFilters}
        type="lookahead"
        filters={filters}
        onChange={handleChangeFilters}
        onHide={() => setShowTaskFilters(false)}
      />

      <ProgrammationManager taskList={lookahead.result} />

      {/* <TasksImporterUnified
        show={showTasksImporter}
        projectId={project._id}
        onHide={() => setShowTasksImporter(false)}
        onImported={handleTaskListImported}
      /> */}

      <PdfExportModal
        show={showPdfExportModal}
        plan="lookahead"
        navigator={navigator}
        project_id={showPdfExportModal ? project_id : undefined}
        items={lookahead.ganttItems}
        nonWorkingDays={nonWorkingDays}
        users={programmation.users}
        filters={filters}
        columns={columns.map((column: any) => {

          let key = column.field

          if (key === 'position') {
            key = 'id'
          } else if (key === 'name') {
            key = 'title'
          }

          return {
            key,
            label: column.label,
            width: column.width
          }
        })}
        collapsedGroups={lookahead.collapsedGroups}
        collapsedTasks={lookahead.collapsedTasks}
        onHide={() => setShowPdfExportModal(false)}
      />
    </ProjectLayout>
  )
}

export default LookaheadPage
